import { useState, useCallback, useEffect } from 'react'
import { Modal } from '@workwhile/ui'
import { RequestWorkersStep } from './steps/RequestWorkersStep'
import { ResultsStep } from './steps/ResultsStep'
import { useAssignmentWorkflowsContextStateValue } from '../ScheduleContent/Calendar/context/AssignmentWorkflowsProvider'
import { useAssignmentDetailContextStateValue } from 'pages/AssignmentDetailPage/context'
import { useRequestWorkersMutation } from 'queries/assignment/useRequestWorkersMutation'
import { useAssignmentShifts } from 'queries/assignment/useAssignmentShifts'
import { toast } from 'react-toastify'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

// Define form schema
const requestWorkersSchema = z.object({
  numWorkers: z.coerce.number().min(1, 'Please enter at least 1 worker'),
})

type RequestWorkersForm = z.infer<typeof requestWorkersSchema>

enum RequestWorkersFlowStep {
  REQUEST_WORKERS = 'REQUEST_WORKERS',
  RESULTS = 'RESULTS',
}

export const RequestWorkersFlow = () => {
  const { assignment, company } = useAssignmentDetailContextStateValue()
  const {
    requestWorkersFlow: {
      isOpen,
      close,
      selectedShifts: contextSelectedShifts,
    },
  } = useAssignmentWorkflowsContextStateValue()
  const { data: shiftsData } = useAssignmentShifts(assignment.id)

  const [currentStep, setCurrentStep] = useState(
    contextSelectedShifts?.length
      ? RequestWorkersFlowStep.REQUEST_WORKERS
      : undefined
  )
  const [selectedShifts, setSelectedShifts] = useState<string[]>(
    contextSelectedShifts || []
  )

  // Update local state when context shifts change
  useEffect(() => {
    if (contextSelectedShifts?.length) {
      setSelectedShifts(contextSelectedShifts)
      setCurrentStep(RequestWorkersFlowStep.REQUEST_WORKERS)
    }
  }, [contextSelectedShifts])

  const requestWorkers = useRequestWorkersMutation({
    companyId: company?.id ?? 0,
    assignmentId: assignment.id,
  })

  const handleClose = useCallback(() => {
    close()
  }, [close])

  // Set up form with react-hook-form
  const methods = useForm<RequestWorkersForm>({
    resolver: zodResolver(requestWorkersSchema),
    defaultValues: {
      numWorkers: 0,
    },
    mode: 'onSubmit',
  })

  const handleConfirmChanges = async (data: RequestWorkersForm) => {
    if (!shiftsData?.shifts) return

    try {
      await requestWorkers.mutateAsync({
        shift_ids: selectedShifts.map(Number),
        additional_workers: data.numWorkers,
      })

      // Only navigate to the Results step after the API call completes successfully
      setCurrentStep(RequestWorkersFlowStep.RESULTS)
    } catch (error) {
      // Display a toast notification for the error
      toast.error(
        'An error occurred while requesting workers. Please try again.'
      )
    }
  }

  const renderStep = () => {
    switch (currentStep) {
      case RequestWorkersFlowStep.REQUEST_WORKERS:
        return (
          <FormProvider {...methods}>
            <RequestWorkersStep
              assignmentId={assignment.id}
              selectedShifts={selectedShifts}
              isLoading={requestWorkers.isPending}
              onSubmit={methods.handleSubmit(handleConfirmChanges)}
            />
          </FormProvider>
        )
      case RequestWorkersFlowStep.RESULTS:
        return (
          <ResultsStep
            selectedShiftIds={selectedShifts}
            success={!requestWorkers.isError}
            error={requestWorkers.error}
            onClose={handleClose}
          />
        )
    }
  }

  return (
    <Modal open={isOpen} onClose={handleClose} width={800}>
      {renderStep()}
    </Modal>
  )
}
